import { useReferenceStore } from "@app/store/useReferenceStore";

/**
 * Имена типов БО, используемые в UI.
 * Реальные коды приходят из `bo.loadBOTypes` и попадают в {@link useReferenceStore} при bootstrap.
 *
 * Список не обязан покрывать весь справочник API — только имена, к которым обращается код.
 */
export type BoTypeName =
    | "Акт инвентаризации"
    | "Акт инвентаризации УП"
    | "Акт проверки наличия"
    | "Внутризаводская накладная"
    | "Внутризаводская накладная УП"
    | "Комплектовочная ведомость"
    | "Лимитная карта"
    | "Накладная на сторону"
    | "Отпуск по лимитной карте"
    | "Приходный ордер";

function getBoTypeMap(): Record<string, number> {
    const types = useReferenceStore.getState().boTypes;
    return Object.fromEntries(types.map((t) => [t.Name, t.Code]));
}

/**
 * Коды типов БО по имени. Данные берутся из {@link useReferenceStore} в момент обращения.
 * Хук стора в компонентах не нужен: используйте `BoType["Приходный ордер"]` и т.д.
 * Если справочник ещё не загружен при bootstrap, обращение по ключу выбросит ошибку.
 *
 * @example
 * ```ts
 * boType={BoType["Приходный ордер"]}
 * ```
 */
export const BoType: { [K in BoTypeName]: number } = new Proxy({} as { [K in BoTypeName]: number }, {
    get(_, prop: string) {
        const map = getBoTypeMap();
        const value = map[prop];
        if (value === undefined) {
            throw new Error(
                `BoType["${prop}"]: справочник типов БО не загружен или тип "${prop}" отсутствует. Проверьте bootstrap и наличие данных в useReferenceStore.`,
            );
        }
        return value;
    },
});

/**
 * Наименование типа БО по коду (данные из {@link useReferenceStore}).
 */
export function getBoTypeName(code: number): string {
    const item = useReferenceStore.getState().boTypes.find((t) => t.Code === code);
    return item?.Name ?? "";
}
